// DLC Auto-Population Engine
// For each DLC row, derive whether it can be auto-populated based on Fraud + PCC responses.
//
// Logic:
// 1. Match DLC rows to Fraud+PCC rows by `assignedDepartment` (primary) and `linkedRiskId` (optional boost).
// 2. If ALL matching Fraud+PCC answered rows for the department are responseType=Yes, auto-populate DLC = Yes.
// 3. If ANY is No/NG/N/A, DLC cannot be auto-populated (manual answer needed).
// 4. Return structured result with provenance so UI can show source Fraud/PCC IDs.

export function computeDlcAutofill({ dlcRows, fraudRows, pccRows, formResponses }) {
  const out = {};
  const byDept = { fraud: {}, pcc: {} };

  const bucket = (mod, rows) => {
    (rows || []).forEach((r) => {
      const dept = r.assignedDepartment;
      if (!dept) return;
      const id = mod === 'fraud' ? r.controlId : r.questionId;
      if (!byDept[mod][dept]) byDept[mod][dept] = [];
      byDept[mod][dept].push({ id, linkedRiskId: r.linkedRiskId, ...r });
    });
  };
  bucket('fraud', fraudRows);
  bucket('pcc', pccRows);

  (dlcRows || []).forEach((d) => {
    const dept = d.assignedDepartment;
    const related = [
      ...((byDept.fraud[dept] || []).map((r) => ({ module: 'fraud', ...r }))),
      ...((byDept.pcc[dept] || []).map((r) => ({ module: 'pcc', ...r }))),
    ];
    const answered = related
      .map((r) => {
        const key = `${r.module}:${r.id}`;
        const resp = formResponses?.[key];
        return resp ? {
          key,
          id: r.id,
          module: r.module,
          responseType: resp.values?.responseType,
          evidenceFile: resp.values?.evidenceFile || null,
          evidenceRequired: r.evidenceRequired || resp.values?.evidenceRequired || 'No',
          submittedBy: resp.submittedBy,
          submittedAt: resp.submittedAt,
        } : null;
      })
      .filter(Boolean);

    const total = related.length;
    const answeredCount = answered.length;
    const allYes = answeredCount > 0 && answered.every((a) => a.responseType === 'Yes');
    const anyNegative = answered.some((a) => ['No', 'N/A', 'NG'].includes(a.responseType));
    const evidenceRefs = answered.filter((a) => a.evidenceRequired === 'Yes' || a.evidenceFile);

    out[d.questionId] = {
      canAutoPopulate: allYes && answeredCount === total && total > 0,
      suggestedResponse: anyNegative ? null : (allYes ? 'Yes' : null),
      sources: answered,
      evidenceRefs,
      total,
      answeredCount,
      needsManual: anyNegative,
      dept,
    };
  });

  return out;
}

export function autoPopStats(autofill) {
  const entries = Object.values(autofill || {});
  const total = entries.length;
  const auto = entries.filter((e) => e.canAutoPopulate).length;
  const needsManual = entries.filter((e) => e.needsManual).length;
  const rate = total ? Math.round((auto / total) * 100) : 0;
  return { total, auto, needsManual, rate };
}
